import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import GoogleSignInButton from '../components/auth/GoogleSignInButton';
import { getConversationInvite, acceptConversationInvite } from '../services/conversationInviteService';

/**
 * Open chat-invite landing — /join-chat/:token. Shows who invited you and to what
 * (public preview), then on Google sign-in joins THAT conversation (not the account)
 * and drops you into the dashboard.
 */
const ConversationInvitePage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, signInWithGoogle } = useAuth();

  const [inviterName, setInviterName] = useState<string | null>(null);
  const [chatTitle, setChatTitle] = useState<string>('a chat');
  const [status, setStatus] = useState<'loading' | 'ready' | 'invalid' | 'joining'>('loading');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) { setStatus('invalid'); return; }
    getConversationInvite(token)
      .then((r) => {
        if (!r.valid) { setStatus('invalid'); return; }
        setInviterName(r.inviterName || 'Someone');
        setChatTitle(r.conversationTitle || 'a chat');
        setStatus('ready');
      })
      .catch(() => setStatus('invalid'));
  }, [token]);

  useEffect(() => {
    if (!token || !user || status !== 'ready') return;
    setStatus('joining');
    acceptConversationInvite(token)
      .then(() => navigate('/dashboard', { replace: true }))
      .catch((e) => { setError(e?.message || 'Could not join this chat'); setStatus('ready'); });
  }, [user, token, status, navigate]);

  const handleSignIn = async () => {
    setError('');
    try { await signInWithGoogle(); } catch (e: any) { setError(e?.message || 'Sign-in failed'); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white dark:bg-black px-6">
      <div className="text-center max-w-sm">
        {status === 'loading' && <p className="text-secondary-500">Loading invitation…</p>}

        {status === 'invalid' && (
          <>
            <h1 className="text-xl font-semibold text-secondary-900 dark:text-white mb-2">Invite not available</h1>
            <p className="text-sm text-secondary-500 dark:text-secondary-400">This invite link is invalid, revoked, or expired. Ask for a fresh one.</p>
          </>
        )}

        {(status === 'ready' || status === 'joining') && (
          <>
            <h1 className="text-xl font-semibold text-secondary-900 dark:text-white mb-2">
              {inviterName} invited you to “{chatTitle}”
            </h1>
            <p className="text-sm text-secondary-500 dark:text-secondary-400 mb-6">
              Join this conversation to chat with their team of specialists. You'll only join this chat — not their account.
            </p>
            {status === 'joining' ? (
              <p className="text-sm text-secondary-500">Joining…</p>
            ) : (
              /* Google-branded, like every other sign-in surface — a visitor should recognise the same
                 button wherever they meet it. "Continue" because this flow is already in progress. */
              <GoogleSignInButton onClick={handleSignIn} label="Continue with Google" />
            )}
            {error && <p className="mt-4 text-sm text-secondary-500 dark:text-secondary-400">{error}</p>}
          </>
        )}
      </div>
    </div>
  );
};

export default ConversationInvitePage;
